import { create } from '@bufbuild/protobuf'
import type { ConnectError } from '@connectrpc/connect'
import { useQuery } from '@connectrpc/connect-query'
import type { AreaCode } from '@ebalance/proto/bfftypes/v1/area_pb'
import type { LicenseType } from '@ebalance/proto/bfftypes/v1/license_type_pb'
import type { ProcurementNeedMarket } from '@ebalance/proto/bffuser/v1/supply_demand_gap_model_pb'
import { getProcurementNeed } from '@ebalance/proto/bffuser/v1/supply_demand_gap_service-SupplyDemandGapService_connectquery'
import {
  GetProcurementNeedRequestSchema,
  type GetProcurementNeedResponse,
} from '@ebalance/proto/bffuser/v1/supply_demand_gap_service_pb'
import { DateSchema } from '@ebalance/proto/google/type/date_pb'
import type { UseQueryResult } from '@tanstack/react-query'

/**
 * 入札後ポジションチャートの調達必要量 (need) 取得 hook。
 * per-area 48 コマ need を返す。frontend でチェック中エリアをコマ別合算して基準線にする。
 * areaCodes が空のときは enabled: false でクエリをスキップする。
 *
 * fail-safe: need の取得失敗は「need 線を描かず注記を出して入札量のみ描画」が仕様
 * (= [[useProcurementNeedChartData]] の `needUnavailable`、 設計書 bidding-position-need.md §7)。
 * global QueryClient の `throwOnError: true` のままだと error boundary (= 全画面エラー) に
 * 飛んで注記どころか画面ごと消えるため、本 query だけ `false` に上書きする。
 */
export function useGetProcurementNeed(
  date: Date,
  areaCodes: AreaCode[],
  market: ProcurementNeedMarket,
  options?: {
    /**
     * false の間は RPC を発火しない (issue #7131 / AC-WFW-006a Decision 1:
     * ワークフローケース対象日が確定するまで対象日依存のデータ取得を開始しない)。
     * 省略時は従来どおり常時有効 (= 後方互換)。areaCodes が空のときの既定
     * gating (`areaCodes.length > 0`) とは AND で合成する。
     */
    enabled?: boolean
    /**
     * 発注主体区分。 指定するとその区分の計画から求めた need を返し、 区分が計画を持たない
     * エリアの series は返らない (AC-BSP-102)。 省略時はエリア全体 (区分なし)。
     */
    issuerType?: LicenseType
  },
): UseQueryResult<GetProcurementNeedResponse, ConnectError> {
  return useQuery(
    getProcurementNeed,
    create(GetProcurementNeedRequestSchema, {
      date: create(DateSchema, {
        year: date.getFullYear(),
        month: date.getMonth() + 1,
        day: date.getDate(),
      }),
      areaCodes,
      market,
      issuerType: options?.issuerType,
    }),
    {
      enabled: areaCodes.length > 0 && (options?.enabled ?? true),
      throwOnError: false,
    },
  )
}
